import React, { useEffect, useState } from "react";
import { CalendarClock, Repeat, Trash2, Plus, Clock, Globe2, Users, UserRound, Power } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError } from "../../lib/api";
import { programs } from "../../mock/mock";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AudiencePicker } from "../../components/admin/AudiencePicker";

const PROGRAM_NAMES = programs.map((p) => p.name);
const DIAS = [["1", "L"], ["2", "M"], ["3", "X"], ["4", "J"], ["5", "V"], ["6", "S"], ["0", "D"]];
const REC_LABEL = { once: "Una vez", daily: "Cada día", weekly: "Semanal", monthly: "Mensual" };
const today = () => new Date().toISOString().slice(0, 10);

const empty = () => ({
  title: "", body: "", url: "/mi-cuenta", target: "all", programa: PROGRAM_NAMES[0], user_ids: [], programas: ["Todos"], grupos: ["Todos"],
  hora: "06:00", recurrencia: "once", dias: [], dia_mes: 1, fecha_inicio: today(), fecha_fin: today(),
});

export default function ScheduledManager({ users }) {
  const res = adminApi.scheduled();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const load = () => res.list().then(setItems).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDia = (d) => setForm((f) => ({ ...f, dias: f.dias.includes(Number(d)) ? f.dias.filter((x) => x !== Number(d)) : [...f.dias, Number(d)] }));
  const toggleUser = (id) => setForm((f) => ({ ...f, user_ids: f.user_ids.includes(id) ? f.user_ids.filter((x) => x !== id) : [...f.user_ids, id] }));

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Completa título y mensaje."); return; }
    if (form.recurrencia === "weekly" && form.dias.length === 0) { toast.error("Elige al menos un día de la semana."); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, body: form.body, url: form.url || "/mi-cuenta",
        target: form.target, hora: form.hora, recurrencia: form.recurrencia,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.recurrencia === "once" ? form.fecha_inicio : form.fecha_fin,
      };
      if (form.target === "segmento") {
        if (!form.programas.length || !form.grupos.length) { toast.error("Elige al menos un programa y un grupo."); setSaving(false); return; }
        payload.programas = form.programas; payload.grupos = form.grupos;
      }
      if (form.target === "usuarios") payload.user_ids = form.user_ids;
      if (form.recurrencia === "weekly") payload.dias = form.dias;
      if (form.recurrencia === "monthly") payload.dia_mes = Number(form.dia_mes);
      await res.create(payload);
      toast.success("Notificación programada.");
      setForm(empty());
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const toggleActivo = async (it) => { try { await res.update(it.id, { activo: !it.activo }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { try { await res.remove(id); toast.success("Eliminada."); load(); } catch (e) { toast.error(apiError(e)); } };

  const segLabel = (it) => `${(it.programas || []).includes("Todos") ? "Todos los programas" : (it.programas || []).join(", ")} · ${(it.grupos || []).includes("Todos") || !(it.grupos || []).length ? "Todos los grupos" : (it.grupos || []).map((g) => `Grupo ${g}`).join(", ")}`;
  const targetLabel = (it) => it.target === "all" ? "Todos" : it.target === "programa" ? it.programa : it.target === "segmento" ? segLabel(it) : `${(it.user_ids || []).length} usuario(s)`;

  return (
    <div className="mt-6 grid lg:grid-cols-2 gap-6" data-testid="scheduled-manager">
      {/* Form */}
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6">
        <h2 className="font-extrabold text-[#0A1C30] mb-1 flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#E9A400]" /> Programar notificación</h2>
        <p className="text-xs text-[#6B7688] mb-4">Se envía a la hora local de cada usuario según su país/ciudad de registro.</p>
        <div className="space-y-4">
          <div><Label className="mb-1.5 block font-bold">Título</Label><Input data-testid="sched-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Recordatorio: reflexión de la mañana" /></div>
          <div><Label className="mb-1.5 block font-bold">Mensaje</Label>
            <textarea data-testid="sched-body" value={form.body} onChange={(e) => set("body", e.target.value)} className="w-full min-h-[70px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="No olvides comentar la reflexión de hoy." />
          </div>
          <div><Label className="mb-1.5 block font-bold">Enlace al abrir (opcional)</Label>
            <Select value={form.url} onValueChange={(v) => set("url", v)}>
              <SelectTrigger data-testid="sched-url"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="/mi-cuenta">Mi panel</SelectItem>
                <SelectItem value="/campus">Campus Supera (clases)</SelectItem>
                <SelectItem value="/graduacion">Puntos Graduación</SelectItem>
                <SelectItem value="/ranking">Ranking</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div><Label className="mb-1.5 block font-bold">Enviar a</Label>
            <div className="flex gap-2">
              {[["all", "Todos", Globe2], ["segmento", "Programa y grupo", Users], ["usuarios", "Usuarios", UserRound]].map(([v, l, Ic]) => (
                <button key={v} data-testid={`sched-target-${v}`} onClick={() => set("target", v)} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 font-bold text-xs transition-all ${form.target === v ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#EDF0F4]"}`}>
                  <Ic className="w-4 h-4" /> {l}
                </button>
              ))}
            </div>
          </div>
          {form.target === "segmento" && (
            <AudiencePicker testid="sched-aud" programas={form.programas} grupos={form.grupos} onChange={(v) => setForm((f) => ({ ...f, ...v }))} />
          )}
          {form.target === "usuarios" && (
            <div className="max-h-40 overflow-y-auto rounded-xl ring-1 ring-black/5 divide-y divide-black/5">
              {users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-[#F4F6F9]">
                  <input type="checkbox" checked={form.user_ids.includes(u.id)} onChange={() => toggleUser(u.id)} />
                  <span className="font-semibold text-[#0A1C30]">{u.nombre} {u.apellidos}</span>
                </label>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block font-bold">Hora (local del usuario)</Label><Input data-testid="sched-hora" type="time" value={form.hora} onChange={(e) => set("hora", e.target.value)} /></div>
            <div><Label className="mb-1.5 block font-bold">Repetición</Label>
              <Select value={form.recurrencia} onValueChange={(v) => set("recurrencia", v)}>
                <SelectTrigger data-testid="sched-rec"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Una sola vez</SelectItem>
                  <SelectItem value="daily">Todos los días</SelectItem>
                  <SelectItem value="weekly">Días de la semana</SelectItem>
                  <SelectItem value="monthly">Mensual (día del mes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.recurrencia === "weekly" && (
            <div className="flex gap-1.5">
              {DIAS.map(([d, l]) => (
                <button key={d} onClick={() => toggleDia(d)} className={`w-9 h-9 rounded-full font-bold text-xs transition-all ${form.dias.includes(Number(d)) ? "bg-[var(--gold)] text-[#0A1C30]" : "bg-[#F4F6F9] text-[#6B7688]"}`}>{l}</button>
              ))}
            </div>
          )}
          {form.recurrencia === "monthly" && (
            <div><Label className="mb-1.5 block font-bold">Día del mes</Label><Input type="number" min="1" max="31" value={form.dia_mes} onChange={(e) => set("dia_mes", e.target.value)} /></div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="mb-1.5 block font-bold">Desde</Label><Input data-testid="sched-inicio" type="date" value={form.fecha_inicio} onChange={(e) => set("fecha_inicio", e.target.value)} /></div>
            {form.recurrencia !== "once" && (
              <div><Label className="mb-1.5 block font-bold">Hasta (máx. 365 días)</Label><Input data-testid="sched-fin" type="date" value={form.fecha_fin} onChange={(e) => set("fecha_fin", e.target.value)} /></div>
            )}
          </div>

          <button data-testid="sched-save-btn" onClick={create} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold py-3 transition-colors disabled:opacity-60">
            <Plus className="w-5 h-5" /> {saving ? "Guardando..." : "Programar"}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl ring-1 ring-black/5 p-6">
        <h2 className="font-extrabold text-[#0A1C30] mb-4">Programadas ({items.length})</h2>
        <div className="space-y-3" data-testid="scheduled-list">
          {items.map((it) => (
            <div key={it.id} data-testid={`sched-${it.id}`} className={`rounded-2xl ring-1 p-4 ${it.activo ? "bg-white ring-black/5" : "bg-[#F4F6F9] ring-black/5 opacity-60"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-extrabold text-[#0A1C30]">{it.title}</p>
                  <p className="text-sm text-[#6B7688] line-clamp-1">{it.body}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button title={it.activo ? "Pausar" : "Activar"} onClick={() => toggleActivo(it)} className={`p-2 rounded-lg ${it.activo ? "text-[#22A860] hover:bg-[#EAF7EF]" : "text-[#B0B6C0] hover:bg-black/5"}`}><Power className="w-4 h-4" /></button>
                  <button data-testid={`sched-del-${it.id}`} onClick={() => remove(it.id)} className="p-2 rounded-lg hover:bg-[#FDECEC] text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-bold text-[#6B7688]">
                <span className="inline-flex items-center gap-1 text-[#3B7DF6]"><Clock className="w-3.5 h-3.5" /> {it.hora}</span>
                <span className="inline-flex items-center gap-1"><Repeat className="w-3.5 h-3.5" /> {REC_LABEL[it.recurrencia]}</span>
                <span>{targetLabel(it)}</span>
                <span>{it.fecha_inicio}{it.recurrencia !== "once" && ` → ${it.fecha_fin || "∞"}`}</span>
              </div>
            </div>
          ))}
          {items.length === 0 && <p className="text-sm text-[#6B7688]">No hay notificaciones programadas.</p>}
        </div>
      </div>
    </div>
  );
}
